import type { DashboardWidgetMetricCondition } from "../model/types"
import { isRecord, readString, readStringArray } from "./actionArgumentReaders"

interface ArgRow {
  key: string
  value: string
}

/** The comparisons a metric condition can use, as the card writes them. */
const OPERATOR_LABELS = new Map([
  [">", ">"],
  ["<", "<"],
  [">=", "≥"],
  ["<=", "≤"],
  ["=", "="],
  ["!=", "≠"],
])

/** One signed `metric_filter` entry, narrowed the way the parser keeps it. */
function readMetricCondition(
  entry: Record<string, unknown>,
): DashboardWidgetMetricCondition | undefined {
  const metric_id = readString(entry, "metric_id")
  const operator = readString(entry, "operator")
  const threshold = entry.value
  if (
    !metric_id ||
    !operator ||
    !OPERATOR_LABELS.has(operator) ||
    (typeof threshold !== "number" && typeof threshold !== "string")
  ) {
    return undefined
  }
  const metric_output_type = readString(entry, "metric_output_type")
  return {
    metric_id,
    operator,
    value: threshold,
    ...(metric_output_type ? { metric_output_type } : {}),
  }
}

/** A chart's metric conditions as signed; malformed entries and unknown operators are dropped. */
export function readMetricFilter(
  args: Record<string, unknown>,
): DashboardWidgetMetricCondition[] | undefined {
  const value = args.metric_filter
  if (!Array.isArray(value)) {
    return undefined
  }
  const conditions = value
    .filter(isRecord)
    .flatMap((entry) => readMetricCondition(entry) ?? [])
  return conditions.length > 0 ? conditions : undefined
}

/**
 * The chart's unit label fits a condition on its own metric only while the
 * chart plots that metric's values, not a count, success rate, or percentage.
 * `avg` on a string metric plots a YES/NO success rate, the way the dashboard
 * contract's binary aggregation defines it. A condition on the chart's own
 * metric carries the metric's output type too and stands in when the widget
 * omits its own.
 */
function chartValueUnit(
  widget: Record<string, unknown>,
  metricOutputType?: string,
): string | undefined {
  const visualization = readString(widget, "visualization_type")
  // Statistic widgets default an omitted aggregation to `avg`.
  const aggregation =
    readString(widget, "aggregation") ??
    (visualization === "statistic" ? "avg" : undefined)
  // The runtime plots a count for pie and histogram widgets regardless of
  // aggregation, and for top-list widgets that omit one.
  if (
    visualization === "pie" ||
    visualization === "histogram" ||
    (visualization === "top-list" && !aggregation) ||
    aggregation === "count" ||
    aggregation === "success" ||
    (aggregation === "avg" &&
      (readString(widget, "metric_output_type") ?? metricOutputType) ===
        "string") ||
    widget.show_as_percentage === true
  ) {
    return undefined
  }
  return readString(widget, "units")
}

function conditionText(
  condition: DashboardWidgetMetricCondition,
  widget: Record<string, unknown>,
): string {
  const metricId = condition.metric_id
  const operator = condition.operator
  const raw = condition.value
  // A label is quoted verbatim: the server compares it exactly.
  const value = typeof raw === "number" ? String(raw) : JSON.stringify(raw)
  const unit =
    typeof raw === "number" && metricId === readString(widget, "metric_id")
      ? chartValueUnit(widget, condition.metric_output_type)
      : undefined
  return `${metricId} ${OPERATOR_LABELS.get(operator) ?? operator} ${value}${unit ? ` ${unit}` : ""}`
}

/**
 * A malformed signed entry still executes — the server defaults its missing
 * parts — so the card shows it with `?` placeholders instead of hiding it.
 */
function placeholderConditionClause(
  entry: Record<string, unknown>,
  widget: Record<string, unknown>,
): string {
  const metricId = readString(entry, "metric_id") || "?"
  const operator = readString(entry, "operator")
  const raw = entry.value
  const value =
    typeof raw === "number"
      ? String(raw)
      : typeof raw === "string"
        ? JSON.stringify(raw)
        : "?"
  const unit =
    typeof raw === "number" && metricId === readString(widget, "metric_id")
      ? chartValueUnit(widget, readString(entry, "metric_output_type"))
      : undefined
  return `${metricId} ${operator ? (OPERATOR_LABELS.get(operator) ?? operator) : "?"} ${value}${unit ? ` ${unit}` : ""}`
}

/** `m1 > 3000 ms and m2 = "YES"`, or undefined when the chart has none. */
function metricFilterClauses(
  widget: Record<string, unknown>,
): string | undefined {
  // The card lists every condition the signed args carry, in signed order:
  // confirmation posts `proposal.args` unchanged and the server keeps a
  // malformed entry (defaulting its missing parts), so hiding one would let
  // a user confirm a condition they never saw. Unreadable parts render as `?`
  // and a value that is not a condition object is shown as signed.
  const filter = widget.metric_filter
  if (filter === undefined || filter === null) {
    return undefined
  }
  if (!Array.isArray(filter)) {
    return JSON.stringify(filter)
  }
  const clauses = filter.map((entry) => {
    if (!isRecord(entry)) {
      return JSON.stringify(entry)
    }
    const condition = readMetricCondition(entry)
    return condition
      ? conditionText(condition, widget)
      : placeholderConditionClause(entry, widget)
  })
  return clauses.length > 0 ? clauses.join(" and ") : undefined
}

/** The create_widget card row for its metric conditions. */
export function summarizeMetricFilter(
  widget: Record<string, unknown>,
): ArgRow[] {
  const clauses = metricFilterClauses(widget)
  return clauses
    ? [
        {
          key: "metric conditions",
          value: `Only conversations where ${clauses}`,
        },
      ]
    : []
}

/** The build_dashboard card: the board's sections and a sample of its widgets, not raw JSON. */
export function summarizeBuildDashboardArgs(
  args: Record<string, unknown>,
): ArgRow[] {
  const widgets = Array.isArray(args.widgets) ? args.widgets : []
  const rows: ArgRow[] = []
  const name = readString(args, "display_name")
  const dashboardId = readString(args, "dashboard_id")
  if (name) {
    rows.push({ key: "dashboard", value: name })
  } else if (dashboardId) {
    rows.push({ key: "dashboard", value: dashboardId })
  }
  const description = readString(args, "description")
  if (description) {
    rows.push({ key: "description", value: description })
  }
  const titled = widgets.filter(isRecord)
  const sections = titled
    .filter((widget) => readString(widget, "widget_type") === "text")
    .map((widget) => readString(widget, "title"))
    .filter((title): title is string => Boolean(title))
  const dataWidgets = titled.filter(
    (widget) => readString(widget, "widget_type") !== "text",
  )
  rows.push({ key: "widgets", value: String(dataWidgets.length) })
  if (sections.length > 0) {
    rows.push({ key: "sections", value: sections.join(", ") })
  }
  const sample = dataWidgets
    .slice(0, 6)
    .map((widget) => readString(widget, "title"))
    .filter((title): title is string => Boolean(title))
  if (sample.length > 0) {
    const more =
      dataWidgets.length > 6 ? `, +${dataWidgets.length - 6} more` : ""
    rows.push({ key: "charts", value: `${sample.join(", ")}${more}` })
  }
  // Every conditioned widget is listed, past the chart sample and whatever its
  // type: a condition changes what the widget counts, so the user must see it
  // before confirming.
  const conditioned = titled.flatMap((widget) => {
    const clauses = metricFilterClauses(widget)
    const title = readString(widget, "title") ?? "?"
    return clauses ? [`${title}: only conversations where ${clauses}`] : []
  })
  conditioned.forEach((value, index) => {
    rows.push({
      key:
        conditioned.length > 1
          ? `metric conditions ${index + 1}`
          : "metric conditions",
      value,
    })
  })
  const agents = new Set<string>()
  for (const widget of dataWidgets) {
    for (const id of readStringArray(widget, "agent_ids") ?? []) {
      agents.add(id)
    }
  }
  if (agents.size > 0) {
    rows.push({ key: "agents", value: [...agents].join(", ") })
  }
  return rows
}
